<template>
  <footer class="no-print" style="background:#000e19; border-top:1px solid rgba(255,255,255,0.07); padding:3rem 2.5rem 1.75rem;">
    <div class="footer-inner">

      <!-- Brand -->
      <div class="footer-brand">
        <img :src="cptWordmark" :alt="appConfig.brandName" style="height:24px; width:auto;" />
        <p>{{ t('footer.tagline') }}</p>
      </div>

      <!-- Product -->
      <div class="footer-col">
        <div class="footer-col-title">{{ t('footer.colProduct') }}</div>
        <ul>
          <li><a href="#" @click.prevent="scrollToBrandSelector">{{ t('footer.linkReport') }}</a></li>
          <li v-if="!store.paymentId"><a href="#" @click.prevent="scrollToExamples">{{ t('footer.linkSample') }}</a></li>
          <li><a href="#" @click.prevent="scrollToHow">{{ t('footer.linkHowItWorks') }}</a></li>
          <li><router-link :to="localePath('/blog')" class="footer-col-link">{{ t('nav.blog') }}</router-link></li>
        </ul>
      </div>

      <!-- Company -->
      <div class="footer-col">
        <div class="footer-col-title">{{ t('footer.colCompany') }}</div>
        <ul>
          <li><button class="footer-col-link" @click="handleSupport">{{ t('nav.support') }}</button></li>
          <li><router-link :to="localePath('/contact')" class="footer-col-link">{{ t('nav.contact') }}</router-link></li>
          <li><router-link :to="localePath('/about')" class="footer-col-link">{{ t('footer.linkAboutUs') }}</router-link></li>
        </ul>
      </div>

      <!-- Legal -->
      <div class="footer-col">
        <div class="footer-col-title">{{ t('footer.colLegal') }}</div>
        <ul>
          <li>
            <button type="button" class="footer-col-link footer-agreement" @click="showServiceAgreement = true">
              {{ t('footer.serviceAgreement') }}
            </button>
          </li>
          <li><router-link :to="localePath('/data-and-privacy')" class="footer-col-link">{{ t('footer.dataPrivacy') }}</router-link></li>
          <li>
            <button type="button" class="footer-col-link" @click="showPrivacyPolicy = true">
              {{ t('footer.privacyPolicy') }}
            </button>
          </li>
          <li>
            <button type="button" class="footer-col-link" @click="showTermsOfService = true">
              {{ t('footer.termsOfService') }}
            </button>
          </li>
          <li>
            <button type="button" class="footer-col-link" @click="reopenCookieConsent">
              {{ t('footer.cookieSettings') }}
            </button>
          </li>
        </ul>
      </div>

    </div>

    <div class="footer-bottom">
      <span>{{ copyrightBase }}</span>
    </div>

    <ServiceAgreement v-if="showServiceAgreement" @close="showServiceAgreement = false" />
    <PrivacyPolicy v-if="showPrivacyPolicy" @close="showPrivacyPolicy = false" />
    <TermsOfService v-if="showTermsOfService" @close="showTermsOfService = false" />
  </footer>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter, RouterLink } from 'vue-router';
import { appConfig } from '@/config';
import { useAppStore } from '@/stores/app';
import { localePath } from '@/router/localePath';
import cptWordmark from '@/assets/images/branding/cpt-wordmark-white.svg';
import ServiceAgreement from '@/components/ServiceAgreement.vue';
import PrivacyPolicy from '@/components/PrivacyPolicy.vue';
import TermsOfService from '@/components/TermsOfService.vue';
import { clearConsent } from '@/utils/analytics';

const { t } = useI18n();
const store = useAppStore();
const route = useRoute();
const router = useRouter();
const showServiceAgreement = ref(false);
const showPrivacyPolicy = ref(false);
const showTermsOfService = ref(false);

const copyrightBase = computed(() => {
  const year = new Date().getFullYear();
  return t('footer.copyright', { year });
});

const handleSupport = () => {
  if (route.name !== 'home') {
    router.push(localePath('/')).then(() => store.navigateToSupport());
    return;
  }
  store.navigateToSupport();
};

const scrollTo = (selector: string) => {
  if (route.name !== 'home') {
    router.push(localePath('/')).then(() => {
      setTimeout(() => {
        const el = document.querySelector(selector);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    });
    return;
  }
  store.setStep('landing');
  setTimeout(() => {
    const el = document.querySelector(selector);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 50);
};

const scrollToBrandSelector = () => {
  if (route.name !== 'home') {
    router.push(localePath('/')).then(() => store.setStep('oauth'));
    return;
  }
  store.setStep('oauth');
};
const scrollToExamples = () => scrollTo('.report-preview-section');
const scrollToHow = () => scrollTo('.how-section');

const reopenCookieConsent = () => {
  clearConsent();
  window.dispatchEvent(new CustomEvent('cpt:show-cookie-consent'));
};
</script>

<style scoped>
.footer-brand p { color: rgba(255,255,255,0.7); font-size: 0.85rem; line-height: 1.5; }
.footer-bottom { color: rgba(255,255,255,0.6); }
.footer-col-title { color: rgba(255,255,255,0.9); font-weight: 700; }

.footer-col a,
.footer-col-link {
  color: rgba(255,255,255,0.75);
  font-size: 0.82rem;
  text-decoration: none;
  transition: color 0.15s;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}
.footer-col a:hover,
.footer-col-link:hover {
  color: #ffffff;
}
</style>
